<template>
  <main class="access-management-page">
    <!-- Header Card -->
        <PageHeader icon="fas fa-user-shield">
      <template #title>
        Akses Bawaan Jabatan
      </template>
      <template #subtitle>
        Atur baseline pekerjaan utama. Role tambahan dikelola sebagai paket terpisah agar tidak tumpang tindih.
      </template>
      <template #actions>
        <b-button variant="outline-primary" @click="$router.push({ path: '/user-managements/security-center', query: { section: 'roles' } })">
          <i class="fas fa-layer-group mr-2"></i>Kelola Role Akses
        </b-button>
      </template>
    </PageHeader>

    <div class="access-management-grid">
      <section class="access-management-panel access-management-panel--subjects">
        <div class="access-management-panel__heading">
          <div>
            <span class="access-management-eyebrow">Daftar jabatan</span>
            <h2>Pilih jabatan</h2>
            <p>{{ totalRows }} jabatan tersedia</p>
          </div>
          <span class="access-management-panel__icon"><i class="fas fa-id-badge"></i></span>
        </div>

        <div class="access-management-tools">
          <b-input-group class="access-management-search">
            <template #prepend><b-input-group-text><b-icon-search /></b-input-group-text></template>
            <b-form-input
              v-model="filter.name"
              type="text"
              placeholder="Cari nama jabatan"
              aria-label="Cari nama jabatan"
              @keyup="filterByName" />
          </b-input-group>
        </div>

        <div class="access-management-table access-management-table--subjects">
          <b-table hover :items="items" :fields="fields" responsive show-empty empty-text="Belum ada data jabatan">
            <template #table-colgroup="scope">
              <col v-for="field in scope.fields" :key="field.key" :style="{ width: field.key === 'actions' ? '92px' : field.key === 'number' ? '58px' : '' }" />
            </template>
            <template #cell(name)="data">
              <div class="access-management-subject">
                <span class="access-management-avatar"><i class="fas fa-briefcase"></i></span>
                <span>{{ data.value }}</span>
              </div>
            </template>
            <template #cell(actions)="data">
              <button
                type="button"
                class="access-management-select"
                :class="{ 'is-selected': position_id == data.item.id }"
                @click="getAccess(data.item.id)">
                <i :class="position_id == data.item.id ? 'fas fa-check' : 'fas fa-arrow-right'"></i>
                {{ position_id == data.item.id ? 'Dipilih' : 'Pilih' }}
              </button>
            </template>
          </b-table>
        </div>

        <footer v-if="items.length != 0" class="access-management-footer">
          <span>{{ items.length }} dari {{ totalRows }} data</span>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            @page-click="pageOnClick" />
        </footer>
      </section>

      <section class="access-management-panel access-management-panel--permissions">
        <div class="access-management-panel__heading access-management-panel__heading--permission">
          <div>
            <span class="access-management-eyebrow">Konfigurasi izin</span>
            <h2>Hak akses sistem</h2>
            <p v-if="username">Jabatan terpilih: <strong>{{ username }}</strong></p>
            <p v-else>Pilih jabatan untuk mengatur akses minimum yang otomatis diwariskan.</p>
          </div>
          <span class="access-management-panel__icon access-management-panel__icon--teal"><i class="fas fa-key"></i></span>
        </div>

        <div v-if="username" class="access-management-tools access-management-tools--permission">
          <b-input-group class="access-management-search">
            <template #prepend><b-input-group-text><b-icon-search /></b-input-group-text></template>
            <b-form-input
              id="position-filter-input"
              v-model="filterAccess"
              type="search"
              placeholder="Cari fitur atau modul"
              aria-label="Cari hak akses" />
          </b-input-group>
          <span class="access-management-legend"><i class="fas fa-check-circle"></i> Aktif</span>
        </div>
        <b-alert v-if="username" show variant="info">
          Semua karyawan dengan jabatan ini otomatis mewarisi izin di bawah. Tugas tambahan diberikan melalui Role Akses, bukan ditambahkan ke baseline jabatan.
        </b-alert>
        <div v-if="username" class="access-catalog-summary">
          <span><strong>{{ permissionStats.modules }}</strong> modul aktif</span>
          <span><strong>{{ permissionStats.permissions }}</strong> hak akses</span>
          <span><strong>{{ permissionStats.active }}</strong> diberikan</span>
        </div>

        <div v-if="!username" class="access-management-empty">
          <span><i class="fas fa-hand-pointer"></i></span>
          <h3>Belum ada jabatan dipilih</h3>
          <p>Pilih salah satu jabatan dari panel kiri untuk mengatur aksesnya.</p>
        </div>

        <div v-else class="access-management-table access-management-table--permissions">
          <b-table
            id="position-access-table"
            hover
            :items="access_lists_display"
            :fields="fieldAccess"
            :filter="filterAccess"
            :tbody-tr-class="accessRowClass"
            responsive
            @filtered="onFiltered">
            <template #table-colgroup="scope">
              <col v-for="field in scope.fields" :key="field.key" :style="{ width: field.key === 'actions' ? '82px' : field.key === 'code' ? '120px' : '' }" />
            </template>
            <template #cell(code)="data">
              <span v-if="data.item.row_type === 'section'" class="access-management-section-title">{{ data.item.name }}</span>
              <span v-else-if="data.item.row_type === 'module'" class="access-management-module-title"><i :class="data.item.module_icon"></i>{{ data.item.name }}</span>
              <span v-else class="access-management-code">{{ data.item.code }}</span>
            </template>
            <template #cell(name)="data">
              <span v-if="data.item.row_type === 'permission'" class="permission-name"><span class="permission-type" :class="'is-' + data.item.permission_type">{{ permissionTypeLabel(data.item.permission_type) }}</span>{{ data.item.name }}</span>
            </template>
            <template #cell(actions)="data">
              <button
                v-if="data.item.row_type === 'permission'"
                type="button"
                class="access-management-toggle"
                :class="data.item.access_status ? 'is-active' : 'is-inactive'"
                :aria-label="data.item.access_status ? 'Nonaktifkan akses' : 'Aktifkan akses'"
                @click="toggleAccess(data.item)">
                <i :class="data.item.access_status ? 'fas fa-check' : 'fas fa-times'"></i>
              </button>
            </template>
          </b-table>
        </div>
        <div v-if="username && accessDraftDirty" class="access-save-bar"><div><strong>{{ pendingChangeCount }} perubahan belum disimpan</strong><small>Perubahan jabatan akan berdampak ke seluruh pengguna terkait.</small></div><b-form-input v-model="accessChangeReason" placeholder="Alasan perubahan (minimal 5 karakter)" /><b-button variant="outline-secondary" @click="cancelAccessDraft">Batal</b-button><b-button variant="primary" @click="previewAccessDraft">Tinjau & simpan</b-button></div>
      </section>
    </div>
    <b-modal id="position-access-preview-modal" title="Tinjau Perubahan Akses Bawaan Jabatan" @ok.prevent="applyAccessDraft" ok-title="Terapkan perubahan" cancel-title="Kembali"><div v-if="accessPreview"><b-alert show variant="info">Perubahan ini berlaku untuk semua pengguna dengan jabatan <strong>{{ username }}</strong>.</b-alert><b-alert :show="accessPreview.conflicts&&accessPreview.conflicts.length>0" variant="warning"><strong>{{ accessPreview.conflicts.length }} konflik pemisahan tugas.</strong></b-alert><div class="preview-columns"><section><h6>Ditambahkan ({{ accessPreview.added.length }})</h6><span v-for="code in accessPreview.added" :key="'a'+code">+ {{ code }}</span><em v-if="!accessPreview.added.length">Tidak ada</em></section><section><h6>Dicabut ({{ accessPreview.removed.length }})</h6><span v-for="code in accessPreview.removed" :key="'r'+code">− {{ code }}</span><em v-if="!accessPreview.removed.length">Tidak ada</em></section></div><p class="mt-3 mb-0"><strong>Alasan:</strong> {{ accessChangeReason }}</p></div></b-modal>
  </main>
</template>

<script>

import module from "@/core/modules/CrudModule.js";
import { buildPermissionRows, permissionSummary } from "@/core/modules/AccessCatalog.js";
import { debouncer } from "@/core/modules/Helper.js"
import api from "@/core/services/api.service.js";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";

export default {
  name: "List",

  data() {
    return {
      // Filter
      filter: {
        name: "",
      },
      filterAccess: "",
      // Pagination
      perPage: 50,
      currentPage: 1,
      totalRows: 0,
      perPageAccess: 100,
      currentPageAccess: 1,
      totalRowsAccess: 1,
      // Table Head
      fields: [
        {
          key: "number",
          label: "#",
          sortable: true,
        },
        {
          key: "name",
          label: "Nama",
          sortable: true,
        },
        { key: "actions", label: "Aksi" },
      ],
      fieldAccess: [
        {
          key: "code",
          label: "Kode",
          sortable: true,
        },
        {
          key: "name",
          label: "Fitur / Modul Sistem",
          sortable: true,
        },
        { key: "actions", label: "Aksi" },
      ],
      // Table Items Doctor
      items: [],
      // Table Items Employee
      itemsEmployee: [],
      // Table Items Access List
      access_lists: [],
      access_lists_children: [],
      access_lists_display: [],
      //
      access_by_user: [],
      originalAccessCodes: [],
      pendingAccessCodes: [],
      accessChangeReason: "",
      accessPreview: null,
      // user name
      username: "",
      position_id: "",
      clinic_name:
        window.configuration == null
          ? "Medhop"
          : window.configuration.clinic_name,
    };
  },

  computed: {
    accessDraftDirty() {
      return JSON.stringify([...this.originalAccessCodes].sort()) !== JSON.stringify([...this.pendingAccessCodes].sort());
    },
    pendingChangeCount() {
      return this.pendingAccessCodes.filter(code => !this.originalAccessCodes.includes(code)).length + this.originalAccessCodes.filter(code => !this.pendingAccessCodes.includes(code)).length;
    },
    permissionStats() {
      return permissionSummary(this.access_lists_display);
    },
  },

  methods: {
    accessRowClass(item) {
      if (!item) return "";
      if (item.row_type === "section") return "access-management-section-row";
      if (item.row_type === "module") return "access-management-module-row";
      return "access-management-permission-row";
    },
    permissionTypeLabel(type) {
      return { view: "Lihat", manage: "Kelola", approval: "Persetujuan" }[type] || "Akses";
    },
    resetArrayOnNew() {
      this.access_lists = [];
      this.access_lists_children = [];
      this.access_lists_display = [];
      this.access_by_user = [];
      this.originalAccessCodes = [];
      this.pendingAccessCodes = [];
      this.accessChangeReason = "";
    },

    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRowsAccess = filteredItems.length;
      this.currentPage = 1;
    },

    // function tabel dokter di sebelah kiri
    async pageOnClick(evt, page) {
      evt.preventDefault();
      this.currentPage = page;
      await this.pagination();
    },

    async pagination() {
      let filterParams = `&name=${this.filter.name}`;
      let response = await module.paginate(
        "positions",
        `?page=${this.currentPage}&page_size=${this.perPage}${filterParams}`
      );
      let pagination = response.meta.pagination;
      this.items = response.data;
      this.totalRows = pagination.total;
    },

    filterByName() {
      debouncer(() => { this.pagination(); }, 500)
    },

    // function tabel hak akses sebelah kanan
    async getAccessList() {
      let filterParams = `&name=${this.filter.name}`;
      let response = await module.paginate(
        "access-lists",
        `?page=${this.currentPage}&page_size=${this.perPage}${filterParams}`
      );
      this.access_lists = response.data;

      this.access_lists_display = [];
      let a, b, c;
      for (a = 0; a < this.access_lists.length; a++) {
        this.access_lists_children = response.data[a].children;
        this.access_lists_display.push(this.access_lists[a]);
        for (b = 0; b < this.access_lists_children.length; b++) {
          this.access_lists_display.push(this.access_lists_children[b]);
          for (c = 0; c < this.access_lists_display.length; c++) {
            if (this.access_lists_display[c].parent_id !== 0) {
              this.access_lists_display[c].number = "-";
            }
          }
        }
      }
      this.totalRowsAccess = this.access_lists_display.length;
    },

    getAccess(id) {
      this.resetArrayOnNew();
      this.getAccessByPosition(id);
    },

    // async getAccessByPosition(id) {
    //   let access_by_user = await module.getAccessByUser("positions/" + id);
    //   this.username = access_by_user.data.name;
    //   this.position_id = access_by_user.data.id;
    //   let access_right = access_by_user.meta.access_right.split(",");

    //   let filterParams = `&name=${this.filter.name}`;
    //   let response = await moduleSaas.paginate(
    //     "access-lists",
    //     `?page=${this.currentPage}&page_size=${this.perPage}${filterParams}`
    //   );
    //   this.access_lists = response.data;
    // },
    async getAccessByPosition(id) {

      const access_by_user = await module.getAccessByUser("positions/" + id);
      this.username = access_by_user.data.name;
      this.position_id = access_by_user.data.id;
      const [catalogResponse, accessResponse] = await Promise.all([
        api.get("access-governance/permissions"),
        api.get("access-governance/positions/" + id + "/access"),
      ]);
      this.access_lists = catalogResponse.data.data || [];
      this.originalAccessCodes = (accessResponse.data.data.codes || []).map(String).filter(code => this.access_lists.some(item => String(item.code) === code && item.assignable));
      this.pendingAccessCodes = [...this.originalAccessCodes];
      this.access_lists_display = buildPermissionRows(this.access_lists, this.pendingAccessCodes);
      this.accessChangeReason = "";
    },

    toggleAccess(item) {
      item.access_status = !item.access_status;
      this.pendingAccessCodes = this.access_lists_display.filter(row => row.row_type === "permission" && row.access_status).map(row => String(row.code));
    },

    cancelAccessDraft() {
      this.access_lists_display.forEach(item => {
        if (item.row_type === "permission") item.access_status = this.originalAccessCodes.includes(String(item.code));
      });
      this.pendingAccessCodes = [...this.originalAccessCodes];
      this.accessChangeReason = "";
    },

    async previewAccessDraft() {
      if (this.accessChangeReason.trim().length < 5) {
        this.$bvToast.toast("Isi alasan perubahan minimal 5 karakter.", { title: "Alasan diperlukan", variant: "warning", solid: true });
        return;
      }
      const response = await api.post("access-governance/positions/" + this.position_id + "/access/preview", { access_codes: this.pendingAccessCodes });
      this.accessPreview = response.data.data;
      this.$bvModal.show("position-access-preview-modal");
    },

    async applyAccessDraft() {
      const response=await api.update("access-governance/positions/" + this.position_id, "access", { access_codes: this.pendingAccessCodes, reason: this.accessChangeReason, acknowledge_conflicts: true });
      this.$bvModal.hide("position-access-preview-modal");
      this.$bvToast.toast(response.data.message || "Hak akses jabatan berhasil diperbarui.", { title: response.data.status === "pending_approval" ? "Menunggu persetujuan" : "Berhasil", variant: response.data.status === "pending_approval" ? "warning" : "success", solid: true });
      await this.getAccessByPosition(this.position_id);
    },

    async attachPosition(id) {
      let targetUser = this.user_id;
      let done = await api
        .post(
          "access-lists/" + id + "/" + this.position_id + "/attach/position"
        )
        .then((response) => {
          this.getAccessByPosition(this.position_id);
          // if (targetUser == getUser().user_id) {
          //   localStorage.setItem("access_right", response.data.access_right);
          //   localStorage.setItem(
          //     "access_right_display",
          //     JSON.stringify(response.data.access_right_display)
          //   );
          //   return true;
          // }
        });
      if (done) {
        // this.$root.$emit("setMenuActive");
        // this.$root.$emit("UpdateSideMenu");
      }
    },

    async detachPosition(id) {
      let targetUser = this.user_id;
      let done = await api
        .delete(
          "access-lists/" + id + "/" + this.position_id + "/detach/position"
        )
        .then((response) => {
          this.getAccessByPosition(this.position_id);
          // if (targetUser == getUser().user_id) {
          //   localStorage.setItem("access_right", response.data.access_right);
          //   localStorage.setItem(
          //     "access_right_display",
          //     JSON.stringify(response.data.access_right_display)
          //   );
          //   return true;
          // }
        });
      if (done) {
        // this.$root.$emit("setMenuActive");
        // this.$root.$emit("UpdateSideMenu");
      }
    },

    async deleteData(id) {
      // Delete Data
      let result = await module.delete("action-types/" + id);
      // If Deleted
      if (result) {
      }
    },
  },

  mounted() {
    // BreadCrumb
    this.$store.dispatch(SET_BREADCRUMB, [{ title: "Akses Bawaan Jabatan" }]);
    // Get Data
    // this.getAccessList();
    this.pagination();
  },
};
</script>

<style lang="scss" src="@/assets/sass/pages/_access-management.scss"></style>
